import React, { useCallback, Suspense, useState, lazy, useEffect } from 'react';
import { connect } from 'react-redux';
// import API from '@aws-amplify/api';

import { Authenticator } from '../auth/Authenticator';
import { RoundLogo } from '../branding/RoundLogo';
import {
   accessLevels,
   authStatuses,
   permissionsStatuses,
} from '../util/constants';
import { getSubscriptionsFromState } from '../util/getSubscriptionsFromState';
import { Button } from './Button';
import { ErrorBoundary } from '../ErrorBoundary';
import { ElementLoading } from './ElementLoading';
import { updateSubscription } from '../app/actions';
import { PermissionsConditionalRender } from '../permissions/PermissionsConditionalRender';
import { ModalContent } from './ModalContent';

const Subscribe = lazy(() => import('../exercises/Subscribe'));

const mapStateToProps = (state) => ({
   activeSubscriptions: getSubscriptionsFromState(state),
   authenticated: state.appData.userData.status === authStatuses.authenticated,
   loadingPermissions:
      state.appData.permissions.status !== permissionsStatuses.loaded,
});

const mapDispatchToProps = {
   updateSubscription,
};

const Prompt = ({ title, message, onConfirm, onCancel }) => (
   <div>
      <h1>{title}</h1>
      <p className="modal-p">{message}</p>
      <div className="modal-button-wrapper-with-dismiss">
         <Button className="button-cta" type="button" onClick={onConfirm}>
            Let&apos;s Do It!
         </Button>

         <Button className="button-dismiss" onClick={onCancel}>
            Not Now
         </Button>
      </div>
   </div>
);

const SignUpPrompt = ({ onCancel, authenticated, message, onNext }) => {
   const [showSignUp, setShowSignUp] = useState(false);

   if (authenticated) {
      return (
         <ModalContent>
            <RoundLogo className="modal-signup-logo" />
            <h1>Welcome!</h1>
            <p className="modal-p">
               I hope that Ear Instructor will be a valuable resource as you
               continue to learn and practice music production.
            </p>
            <p className="modal-p">
               <strong>Thank you for joining!</strong>
            </p>
            {/* don't display next button until fulll logged in (checked subscription) */}
            <div className="modal-button-wrapper-with-dismiss">
               <PermissionsConditionalRender
                  allowedStatuses={[permissionsStatuses.loaded]}
               >
                  <Button className="button-cta" onClick={onNext}>
                     Next
                  </Button>
               </PermissionsConditionalRender>
               <PermissionsConditionalRender
                  deniedStatuses={[permissionsStatuses.loaded]}
               >
                  <Button className="button-cta button-loading" disabled>
                     Next
                  </Button>
               </PermissionsConditionalRender>
            </div>
         </ModalContent>
      );
   }

   if (showSignUp) {
      return (
         <ModalContent>
            <RoundLogo className="modal-signup-logo" />
            <Authenticator initialView="signUp" />
         </ModalContent>
      );
   }

   return (
      <ModalContent>
         <Prompt
            title="Sign Up?"
            message={message}
            onConfirm={() => setShowSignUp(true)}
            onCancel={onCancel}
         />
      </ModalContent>
   );
};

const SubscribePrompt = ({
   onCancel,
   hasRequiredSubscription,
   message,
   onNext,
   updateSubscription,
}) => {
   const [showSubscribe, setShowSubscribe] = useState(false);
   const [plans, setPlans] = useState(null);
   useEffect(() => {
      // API.get('app', '/purchase/plans').then(setPlans);
   }, []);

   if (hasRequiredSubscription) {
      return (
         <ModalContent>
            <h1>Success!</h1>
            <p className="modal-p">
               A subscription has been added to your account. Thank you for your
               support!
            </p>
            <div className="modal-button-wrapper-with-dismiss">
               <Button className="button-cta" onClick={onNext}>
                  Next
               </Button>
            </div>
         </ModalContent>
      );
   }

   if (showSubscribe) {
      return (
         <ErrorBoundary>
            <Suspense fallback={<ElementLoading />}>
               <ModalContent>
                  <Subscribe
                     onSubscribe={(token) => updateSubscription(token)}
                     plans={plans}
                  />
               </ModalContent>
            </Suspense>
         </ErrorBoundary>
      );
   }

   return (
      <ModalContent>
         <Prompt
            title="Subscribe?"
            message={message}
            onConfirm={() => setShowSubscribe(true)}
            onCancel={onCancel}
         />
      </ModalContent>
   );
};

const accessLevelRanks = {
   unauthenticated: 0,
   authenticated: 1,
   exercise: 2,
   course: 3,
};

const getCurrentAccessLevel = (authenticated, activeSubscriptions) => {
   if (!authenticated) {
      return accessLevels.unauthenticated;
   }
   if (activeSubscriptions.course) {
      return accessLevels.course;
   }
   if (activeSubscriptions.exercise) {
      return accessLevels.exercise;
   }
   return accessLevels.authenticated;
};

const RequireAccessLevel = connect(
   mapStateToProps,
   mapDispatchToProps
)(
   ({
      requiredAccessLevel,
      authenticated,
      activeSubscriptions,
      resourceName,
      onCancel,
      onAccessAllowed,
      updateSubscription,
   }) => {
      const currentAccessLevel = getCurrentAccessLevel(
         authenticated,
         activeSubscriptions
      );

      const [dislayForAccess, setDisplayForAccess] =
         useState(currentAccessLevel);

      const actionMessage =
         accessLevelRanks[currentAccessLevel] <
         accessLevelRanks[accessLevels.authenticated]
            ? 'Please sign up or log in to continue.'
            : 'Please subscribe to continue.';

      const message =
         accessLevelRanks[requiredAccessLevel] >
         accessLevelRanks[accessLevels.authenticated]
            ? `This ${resourceName} is not available without a subscription to Ear Instructor. ${actionMessage}`
            : `You must have an Ear Instructor account to access this ${resourceName}. ${actionMessage}`;

      const handleNext = useCallback(() => {
         if (
            accessLevelRanks[currentAccessLevel] >=
            accessLevelRanks[requiredAccessLevel]
         ) {
            onAccessAllowed();
         } else {
            setDisplayForAccess(currentAccessLevel);
         }
      }, [currentAccessLevel, onAccessAllowed, requiredAccessLevel]);

      if (dislayForAccess === accessLevels.unauthenticated) {
         return (
            <SignUpPrompt
               authenticated={authenticated}
               onNext={handleNext}
               onCancel={onCancel}
               message={message}
            />
         );
      }

      return (
         <SubscribePrompt
            updateSubscription={updateSubscription}
            onNext={handleNext}
            onCancel={onCancel}
            message={message}
            hasRequiredSubscription={
               accessLevelRanks[currentAccessLevel] >=
               accessLevelRanks[requiredAccessLevel]
            }
         />
      );
   }
);

export { RequireAccessLevel };
